// Who could this call be? Reads GET calls/{id}/lead-suggestions/ (CU-869fmc59c): leads that
// share the number, look like it, are archived with it, or were created recently, plus a
// name / phone / email search when `query` has 2+ characters. An older backend (404) falls
// back to the plain leads search, archived leads included.
import { useEffect, useRef, useState } from 'react';
import { callApi } from '../../utils/apiCall';
import { fallbackSearchUrl, readSuggestedLead, readSuggestions, suggestionsUrl } from './callFlowModel';

const READ_OPTIONS = { suppressServerErrorDispatch: true, timeout: 15000 };
export const SUGGESTION_SEARCH_DEBOUNCE_MS = 350;
export const MIN_SEARCH_LENGTH = 2;

// { suggestions, results, fallback } or { error: true }; never throws.
export async function loadLeadSuggestions(practiceId, call, query = '') {
    const q = String(query || '').trim();
    const response = await callApi(suggestionsUrl(practiceId, call.id, q.length >= MIN_SEARCH_LENGTH ? q : ''), 'GET', null, false, READ_OPTIONS);
    if (response?.ok) return { ...readSuggestions(response.data), fallback: false };
    if (response?.status === 404 && (q || call.number)) {
        const search = await callApi(fallbackSearchUrl(practiceId, q.length >= MIN_SEARCH_LENGTH ? q : call.number), 'GET', null, false, READ_OPTIONS);
        if (search?.ok) {
            const leads = (Array.isArray(search.data?.results) ? search.data.results : []).map(readSuggestedLead).filter(Boolean);
            // Without a typed query these are the number's matches: show them as the suggestions.
            return q.length >= MIN_SEARCH_LENGTH
                ? { suggestions: [], results: leads, fallback: true }
                : { suggestions: leads.map((lead) => ({ lead, reasons: [] })), results: [], fallback: true };
        }
    }
    return { error: true };
}

export default function useLeadSuggestions({ practiceId, call, query = '' }) {
    const [state, setState] = useState({ status: 'loading', suggestions: [], results: [], fallback: false });
    const request = useRef(0);
    const callId = call?.id;

    useEffect(() => {
        if (!practiceId || !callId) return undefined;
        request.current += 1;
        const mine = request.current;
        const typed = String(query || '').trim().length >= MIN_SEARCH_LENGTH;
        const timer = setTimeout(async () => {
            const data = await loadLeadSuggestions(practiceId, call, query);
            if (request.current !== mine) return;
            if (data.error) setState((prev) => ({ ...prev, status: 'error' }));
            else setState({ status: 'ready', suggestions: data.suggestions, results: data.results, fallback: data.fallback });
        }, typed ? SUGGESTION_SEARCH_DEBOUNCE_MS : 0);
        return () => { clearTimeout(timer); };
    // The call object changes identity on every parent render; its id is what matters.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [practiceId, callId, query]);

    // Typing again shows the loading line, not the previous results as if they were new.
    useEffect(() => { setState((prev) => (prev.status === 'ready' ? { ...prev, status: 'loading' } : prev)); }, [query]);

    return state;
}
